/**
 * TestRunPanel - Pannello per testare flow automatici (scheduled/webhook)
 * 
 * Usato per flow che NON hanno un nodo START ma partono da:
 * - Schedule Trigger
 * - Webhook Trigger
 * - RSS Feed
 */

import { useState } from 'react';
import { X, Play, Loader2, CheckCircle, XCircle, Clock, Zap, FileJson } from 'lucide-react';
import { API_URL } from '../config';

interface TestRunPanelProps {
  flowDefinition: {
    id: string;
    nodes: any[];
    edges: any[];
  };
  flowName: string;
  onClose: () => void;
}

interface TestResult {
  success: boolean;
  execution_time_ms: number;
  nodes_executed: number;
  results: Array<{
    node_id: string;
    node_type: string;
    label: string;
    success: boolean;
    output?: string;
    error?: string;
    next_node?: string;
  }>;
  final_variables: Record<string, any>;
  errors: string[];
}

export default function TestRunPanel({ flowDefinition, flowName, onClose }: TestRunPanelProps) {
  const [isRunning, setIsRunning] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [showVariables, setShowVariables] = useState(false);
  const [mockData, setMockData] = useState<string>('{}');

  // Trova il tipo di trigger
  const triggerTypes = ['schedule_trigger', 'webhook_trigger', 'rss_feed'];
  const triggerNode = flowDefinition.nodes.find(n => triggerTypes.includes(n.type));
  const triggerType = triggerNode?.type || 'unknown';

  const runTest = async () => {
    setIsRunning(true);
    setTestResult(null);

    try {
      let mockTriggerData = {};
      try {
        mockTriggerData = JSON.parse(mockData);
      } catch {
        // Ignora errori di parsing, usa oggetto vuoto
      }

      const response = await fetch(`${API_URL}/flow/test-run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flow_definition: flowDefinition,
          mock_trigger_data: Object.keys(mockTriggerData).length > 0 ? mockTriggerData : null,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result: TestResult = await response.json();
      setTestResult(result);
    } catch (error) {
      console.error('Test run error:', error);
      setTestResult({
        success: false,
        execution_time_ms: 0,
        nodes_executed: 0,
        results: [],
        final_variables: {},
        errors: [`Errore: ${error}`],
      });
    } finally {
      setIsRunning(false);
    }
  };

  const getTriggerIcon = () => {
    switch (triggerType) {
      case 'schedule_trigger': return '⏰';
      case 'webhook_trigger': return '🔗';
      case 'rss_feed': return '📰';
      default: return '🚀';
    }
  };

  const getTriggerName = () => {
    switch (triggerType) {
      case 'schedule_trigger': return 'Schedule Trigger';
      case 'webhook_trigger': return 'Webhook Trigger';
      case 'rss_feed': return 'RSS Feed';
      default: return 'Flow Automatico';
    }
  };

  return (
    <div className="fixed right-0 top-0 bottom-0 w-[500px] bg-gradient-to-br from-slate-900 to-slate-800 
                    shadow-2xl z-50 flex flex-col border-l border-slate-700">
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex items-center justify-between bg-gradient-to-r from-purple-900/50 to-indigo-900/50">
        <div className="flex items-center gap-3">
          <div className="text-3xl">{getTriggerIcon()}</div>
          <div>
            <h2 className="text-lg font-bold text-white">Test Run</h2>
            <p className="text-sm text-slate-400">{getTriggerName()}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-slate-700 rounded-lg transition text-slate-400 hover:text-white"
        >
          <X size={24} />
        </button>
      </div>

      {/* Info Flow */}
      <div className="p-4 bg-slate-800/50 border-b border-slate-700">
        <div className="flex items-center gap-2 text-sm text-slate-300">
          <FileJson size={16} />
          <span className="font-mono">{flowName}</span>
        </div>
        <div className="flex gap-4 mt-2 text-xs text-slate-400">
          <span>📦 {flowDefinition.nodes.length} nodi</span>
          <span>🔗 {flowDefinition.edges.length} connessioni</span>
        </div>
      </div>

      {/* Mock Data Input */}
      <div className="p-4 border-b border-slate-700">
        <label className="block text-sm font-medium text-slate-300 mb-2">
          📋 Dati Mock (opzionale)
        </label>
        <textarea
          value={mockData}
          onChange={(e) => setMockData(e.target.value)}
          placeholder='{"rss_items": [...], "webhook_data": {...}}'
          className="w-full h-24 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                     text-white text-sm font-mono resize-none
                     focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
        <p className="text-xs text-slate-500 mt-1">
          JSON con dati da passare al flow (es. rss_items, webhook_data)
        </p>
      </div>

      {/* Run Button */}
      <div className="p-4 border-b border-slate-700">
        <button
          onClick={runTest}
          disabled={isRunning}
          className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 
                     hover:from-purple-500 hover:to-indigo-500 
                     disabled:from-slate-600 disabled:to-slate-600
                     text-white font-bold rounded-xl transition-all
                     flex items-center justify-center gap-2 shadow-lg"
        >
          {isRunning ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              <span>Esecuzione in corso...</span>
            </>
          ) : (
            <>
              <Play size={20} />
              <span>Esegui Test</span>
            </>
          )}
        </button>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto p-4">
        {testResult ? (
          <div className="space-y-4">
            {/* Summary */}
            <div className={`p-4 rounded-xl ${testResult.success 
              ? 'bg-green-900/30 border border-green-500/50' 
              : 'bg-red-900/30 border border-red-500/50'}`}>
              <div className="flex items-center gap-3">
                {testResult.success ? (
                  <CheckCircle className="text-green-400" size={24} />
                ) : (
                  <XCircle className="text-red-400" size={24} />
                )}
                <div>
                  <h3 className={`font-bold ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>
                    {testResult.success ? 'Test Completato ✅' : 'Test Fallito ❌'}
                  </h3>
                  <div className="flex gap-4 text-sm text-slate-400 mt-1">
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {testResult.execution_time_ms}ms
                    </span>
                    <span className="flex items-center gap-1">
                      <Zap size={14} />
                      {testResult.nodes_executed} nodi
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Errors */}
            {testResult.errors.length > 0 && (
              <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
                <h4 className="text-red-400 font-semibold mb-2">❌ Errori</h4>
                {testResult.errors.map((error, i) => (
                  <p key={i} className="text-red-300 text-sm">{error}</p>
                ))}
              </div>
            )}

            {/* Node Results */}
            {testResult.results.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-slate-300 font-semibold">📋 Esecuzione Nodi</h4>
                {testResult.results.map((result, i) => (
                  <div 
                    key={i} 
                    className={`p-3 rounded-lg border ${result.success 
                      ? 'bg-slate-800 border-slate-700' 
                      : 'bg-red-900/20 border-red-500/30'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">
                          {result.success ? '✅' : '❌'}
                        </span>
                        <span className="font-mono text-sm text-purple-400">
                          {result.node_type}
                        </span>
                        <span className="text-slate-400 text-sm">
                          {result.label}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500 font-mono">
                        {result.node_id.slice(0, 8)}...
                      </span>
                    </div>
                    {result.output && (
                      <div className="mt-2 p-2 bg-slate-900 rounded text-xs text-slate-300 font-mono overflow-x-auto">
                        {result.output.slice(0, 200)}
                        {result.output.length > 200 && '...'}
                      </div>
                    )}
                    {result.error && (
                      <div className="mt-2 p-2 bg-red-900/30 rounded text-xs text-red-300">
                        {result.error}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Variables Toggle */}
            <button
              onClick={() => setShowVariables(!showVariables)}
              className="w-full py-2 text-sm text-slate-400 hover:text-slate-200 transition"
            >
              {showVariables ? '▼ Nascondi Variabili' : '▶ Mostra Variabili Finali'}
            </button>

            {showVariables && Object.keys(testResult.final_variables).length > 0 && (
              <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <h4 className="text-slate-300 font-semibold mb-2">📦 Variabili Finali</h4>
                <pre className="text-xs text-slate-300 font-mono overflow-x-auto max-h-60 overflow-y-auto">
                  {JSON.stringify(testResult.final_variables, null, 2)}
                </pre>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-slate-400 py-8">
            <Play size={48} className="mx-auto mb-4 opacity-30" />
            <p>Clicca "Esegui Test" per simulare l'esecuzione del flow</p>
            <p className="text-sm mt-2 text-slate-500">
              Il flow verrà eseguito con dati di test simulati
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

